import {
	Checkbox,
	Form,
	FormItem
} from "client/Components";
import {
	AddTilesResponseModel,
	EmbedAppDetailsTileModel,
	EmbedAppUpdateTileModel,
	updateEmbedAppTile
} from "client/Api/Workspace/workspace";
import { IFormHandler, useFormHandler } from "client/Forms";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import {
	isNotNullNorUndefined,
	isNotEmpty,
	doesNotExceedLength
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useImperativeHandle,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { ExistingTile } from "./common";
import { EditTileFromData, initialGenericState } from "./EditGenericTile";
import { EditTileHandle } from "./EditTile";
import { EditTileGeneralFields } from "./GeneralFields";
import { useUpdateTile } from "./hooks/useUpdateTile";

export type EditEmbedAppTileProps = {
	isLoading: boolean;
	onIsLoadingChange: (value: boolean) => void;
	initialModel: EmbedAppDetailsTileModel;
	mode: ExistingTile;
	onAfterSave: (response?: AddTilesResponseModel) => void;
};

export const EditEmbedAppTile = forwardRef(
	(props: EditEmbedAppTileProps, ref: Ref<EditTileHandle>): ReactElement => {
		const {
			isLoading,
			onIsLoadingChange,
			initialModel,
			mode,
			onAfterSave
		} = props;

		const [formData, setFormData] = useState<EmbedAppEditFormData>({
			...initialGenericState(initialModel),
			hideHeader: initialModel.hideHeader,
			hideFooter: initialModel.hideFooter
		});

		const formHandler: IFormHandler<EmbedAppEditFormData> = useFormHandler(
			formData,
			setFormData,
			validationBuilder
		);

		const { updateHandler } = useUpdateTile<EmbedAppUpdateTileModel>(
			formHandler,
			updateEmbedAppTile,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave
		);

		useImperativeHandle(
			ref,
			(): EditTileHandle => ({
				updateHandler
			})
		);

		const isLiveInformationDimension: boolean = formData.dimension
			.columnSpan >= 4;

		return (
			<Form
				onSubmit="NONE"
			>
				<EditTileGeneralFields
					initialModel={initialModel}
					formHandler={formHandler}
				/>

				{isLiveInformationDimension && (
					<>
						<FormItem>
							<Checkbox
								{...formHandler.for("hideHeader").attachToCheckbox()}
								text={localize("_HideTileHeader")}
							/>
						</FormItem>

						<FormItem>
							<Checkbox
								{...formHandler.for("hideFooter").attachToCheckbox()}
								text={localize("_HideTileFooter")}
							/>
						</FormItem>
					</>
				)}

			</Form>
		);
	}
);
EditEmbedAppTile.displayName = "EditEmbedAppTile";

const validationBuilder: IValidationConfiguration<EmbedAppEditFormData> = createValidationConfiguration<EmbedAppEditFormData>();

validationBuilder.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

type EmbedAppEditFormData = EditTileFromData<EmbedAppUpdateTileModel>;
